import { useMutation, useQueryClient } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"
import type {
  ResponseDto,
  CreateOrganizationDto,
  OrganizationDto,
} from "@mimber/schema"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { organizationsKeys } from "../organizations.utils"

/**
 * Hook for creating a new organization
 * @param options - Mutation options
 * @returns Mutation result with organization data
 */
export function useCreateOrganization(
  options?: UseMutationOptions<
    ResponseDto<OrganizationDto>,
    Error,
    CreateOrganizationDto
  >
): UseMutationResult<
  ResponseDto<OrganizationDto>,
  Error,
  CreateOrganizationDto
> {
  guardAppUsage("mimber-organization", "useCreateOrganization")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (data: CreateOrganizationDto) => {
      const response = await server.post<ResponseDto<OrganizationDto>>(
        "/organizations",
        data
      )
      return response.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate organizations list to refresh with new organization
      queryClient.invalidateQueries({
        queryKey: organizationsKeys.findAll(),
      })
      if (options?.onSuccess) {
        options.onSuccess(data, variables, onMutateResult, context)
      }
    },
  })
}
